import axios from 'axios';
import storageManager from './storageManager';
import { isGuestPublicPath } from '@/common/utils/guestRoutes';

const X_CLIENT_TYPES = {
	BROWSER: 'BROWSER',
	BROWSER_MOBILE: 'BROWSER_MOBILE',
};

class Api {
	constructor() {
		this.axios = this.createAxios();
		storageManager.auth.consumeTransferFromHash();
		const token = storageManager.auth.getToken();
		if (token) {
			this.axios.defaults.headers.common['authorization'] = `Bearer ${token}`;
		}
		this.useInterceptors();
	}

	shouldRedirectToLogin(status) {
		if (status !== 401 && status !== 403) {
			return false;
		}

		if (isGuestPublicPath()) {
			return false;
		}

		return Boolean(storageManager.auth.getToken());
	}

	handleAuthError(error) {
		const status = error?.response?.status;
		if (this.shouldRedirectToLogin(status)) {
			storageManager.auth.clearAuth(this.axios);
			window.location = '/';
			return Promise.reject(error);
		}

		if (status) {
			const wrappedError = new Error(
				error?.response?.data?.message ?? 'Request failed',
			);
			wrappedError.response = error.response;
			throw wrappedError;
		}

		return Promise.reject(error);
	}

	useInterceptors() {
		this.axios.interceptors.request.use(
			(request) => {
				const token = storageManager.auth.getToken();
				if (token) {
					request.headers.authorization = `Bearer ${token}`;
				}

				const isMobile = window?.innerWidth <= 480;
				request.headers['x-client-platform'] = isMobile
					? X_CLIENT_TYPES.BROWSER_MOBILE
					: X_CLIENT_TYPES.BROWSER;

				return request;
			},
			(error) => this.handleAuthError(error),
		);
		this.axios.interceptors.response.use(
			(response) => response,
			(error) => this.handleAuthError(error),
		);
	}

	createAxios() {
		const baseURL = import.meta.env.VITE_BACKEND_URL;

		if (!baseURL) {
			throw new Error('VITE_BACKEND_URL environment variable is not defined');
		}

		return axios.create({ baseURL });
	}
}

const api = new Api();

export default api;
